import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { createPaginatedEndpoint } from "../utils/services.util";
import { PaginatedResponse } from "./common";
import { emptySplitApi } from "./empty-split-api";
import { FaxFilters, FaxResponse, SendFaxInput } from "./types/fax.type";

type SendFaxResponse = any;

export const FAXES_QUERY =
  "{id,direction,subject,sender_name,recipient_name,recipient,created_at,document,status,clinic{id,name,fax_phone},assigned_tags,patient,message,deactivated_by{id,first_name,last_name,name},deactivated_at}";

const faxesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    sendFax: builder.mutation<SendFaxResponse, SendFaxInput>({
      query: ({
        recipient,
        documents,
        clinic,
        patient,
        box_file_ids,
        include_cover_page,
        notes,
        subject,
        recipient_name,
        sender_name
      }) => {
        const formData = new FormData();
        formData.append("recipient", recipient);
        formData.append("clinic", String(clinic || ""));

        if (patient) {
          formData.append("patient", String(patient));
        }

        if (documents) {
          documents.forEach((document: File, index: number) => {
            formData.append(`documents[${index}]`, document);
          });
        }

        if (patient && box_file_ids) {
          formData.append("box_file_ids", String(box_file_ids));
        }

        if (include_cover_page) {
          formData.append("include_cover_page", "true");
        }

        if (notes) {
          formData.append("notes", notes);
        }

        if (subject) {
          formData.append("subject", subject);
        }

        if (recipient_name) {
          formData.append("recipient_name", recipient_name);
        }

        if (sender_name) {
          formData.append("sender_name", sender_name);
        }

        return {
          url: `/faxes/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["Faxes"]
    }),
    getFaxesPaginated: createPaginatedEndpoint<FaxFilters, FaxResponse[]>(
      builder,
      {
        queryFn: (params) => ({
          url: `/faxes/?${formatValidQueryParamUrlCommaSeparated(params)}`
        }),
        providesTags: ["Faxes"],
        transformResponseFn: (response: PaginatedResponse<FaxResponse>) =>
          response.results
      }
    ),
    updateFax: builder.mutation<
      FaxResponse,
      {
        id: number;
        patientId: number;
      }
    >({
      query: ({ id, patientId }) => ({
        url: `/faxes/${id}/`,
        method: "PATCH",
        body: { patient: patientId }
      }),
      invalidatesTags: ["Faxes"]
    }),
    resendFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/`,
        method: "PATCH",
        body: { status: 1 }
      }),
      invalidatesTags: ["Faxes"]
    }),
    deactivateFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Faxes"]
    }),
    restoreFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/restore/`,
        method: "POST"
      }),
      invalidatesTags: ["Faxes"]
    }),
    downloadFax: builder.mutation<FaxResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/faxes/${id}/document/`,
        method: "PATCH"
      }),
      invalidatesTags: ["Faxes"]
    })
  })
});

export const {
  useSendFaxMutation,
  useUpdateFaxMutation,
  useResendFaxMutation,
  useDeactivateFaxMutation,
  useRestoreFaxMutation,
  useDownloadFaxMutation
} = faxesApi;
